import { DataTable, Switch, ToggleGroup, ToggleGroupItem } from '@/components'
import { PATCH_USER_CHILDCARE_MUTATION } from '@/modules/users/mutations'
import { USERS_CHILDCARE_QUERY } from '@/modules/users/queries'
import { UserProfileLink } from '@/modules/users/UserProfile/UserProfileLink'
import UserThumbOrInitials from '@/modules/users/UserThumbOrInitials'
import { getUserLabel } from '@/modules/users/util'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import type { ColumnDef } from '@tanstack/react-table'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import { Suspense, useCallback, useMemo } from 'react'
import { Section } from '../common'

type User = ResultOf<typeof USERS_CHILDCARE_QUERY>['allUsersList'][number]

interface ChildcareTableData {
  id: string
  user: User
  singleParent: boolean
  childChronicIllness: boolean
  numberOfChildren: number
}

interface ChildcareCardInnerProps {
  onToggleSingleParent(user: User, checked: boolean): void
  onToggleChildChronicIllness(user: User, checked: boolean): void
  onChangeNumberOfChildren(user: User, value: string): void
}

export function ChildcareCardInner({
  onToggleSingleParent,
  onToggleChildChronicIllness,
  onChangeNumberOfChildren,
}: ChildcareCardInnerProps) {
  const translations = useTranslate({
    user: 'settings.absence.childcare.user',
    singleParent: 'settings.absence.childcare.single-parent',
    illness: 'settings.absence.childcare.illness',
    numberOfChildren: 'settings.absence.childcare.number-of-children',

    tooltip: {
      singleParent: 'settings.absence.childcare.tooltip.single-parent',
      illness: 'settings.absence.childcare.tooltip.illness',
      numberOfChildren: 'settings.absence.childcare.tooltip.number-of-children',
    },

    numUsers: ['settings.absence.childcare.num-users', { n: 0 }],
  })

  const { data: users } = useSuspenseQuery(USERS_CHILDCARE_QUERY)

  const tableColumns = useMemo<ColumnDef<ChildcareTableData>[]>(
    () => [
      {
        accessorKey: 'id',
        header: '',
        enableSorting: false,
        meta: {
          narrow: true,
        },
        cell: ({ row }) => {
          const user = row.original.user

          return <UserThumbOrInitials user={user} />
        },
      },
      {
        accessorKey: 'user',
        header: translations.user,
        enableSorting: true,
        sortingFn: (a, b) =>
          a.original.user.lastName.localeCompare(b.original.user.lastName),
        cell: ({ row }) => {
          const user = row.original.user

          return (
            <UserProfileLink user={user} userNameTransform={getUserLabel} />
          )
        },
      },
      {
        accessorKey: 'singleParent',
        header: translations.singleParent,
        enableSorting: true,
        meta: {
          center: true,
          narrow: true,
        },
        cell: ({ row }) => {
          const user = row.original.user

          return (
            <Switch
              checked={user.childcare.singleParent}
              onCheckedChange={checked => onToggleSingleParent(user, checked)}
            />
          )
        },
      },
      {
        accessorKey: 'childChronicIllness',
        header: translations.illness,
        enableSorting: true,
        meta: {
          center: true,
          narrow: true,
        },
        cell: ({ row }) => {
          const user = row.original.user

          return (
            <Switch
              checked={user.childcare.childChronicIllness}
              onCheckedChange={checked =>
                onToggleChildChronicIllness(user, checked)
              }
            />
          )
        },
      },
      {
        accessorKey: 'numberOfChildren',
        header: translations.numberOfChildren,
        enableSorting: true,
        meta: {
          centerHeader: true,
          narrow: true,
        },
        cell: ({ row }) => {
          const user = row.original.user

          return (
            <ToggleGroup
              className="justify-self-center"
              value={String(user.childcare.numberOfChildren)}
              type="single"
              variant="outline"
              aria-label="Number of Children"
              onValueChange={value => onChangeNumberOfChildren(user, value)}
            >
              <ToggleGroupItem value="0" aria-label="0 children">
                0
              </ToggleGroupItem>

              <ToggleGroupItem value="1" aria-label="1 child">
                1
              </ToggleGroupItem>

              <ToggleGroupItem value="2" aria-label="2 children">
                2
              </ToggleGroupItem>

              <ToggleGroupItem value="3" aria-label="3 or more children">
                3+
              </ToggleGroupItem>
            </ToggleGroup>
          )
        },
      },
    ],
    [
      onChangeNumberOfChildren,
      onToggleChildChronicIllness,
      onToggleSingleParent,
      translations.illness,
      translations.numberOfChildren,
      translations.singleParent,
      translations.user,
    ]
  )

  const tableData = useMemo<ChildcareTableData[]>(
    () =>
      users.allUsersList.map(user => ({
        id: user.id,
        user,
        singleParent: user.childcare.singleParent,
        childChronicIllness: user.childcare.childChronicIllness,
        numberOfChildren: user.childcare.numberOfChildren,
      })),
    [users]
  )

  return (
    <DataTable
      columns={tableColumns}
      data={tableData}
      pagination={{
        itemsText: n => translations.numUsers({ n }),
      }}
      initialSort={[{ id: 'user', desc: false }]}
    />
  )
}

export function ChildcareCard() {
  const translations = useTranslate({
    header: 'settings.absence.childcare.header',
  })

  const [patchUser, { loading }] = useMutation(PATCH_USER_CHILDCARE_MUTATION)

  const handleToggleSingleParent = useCallback(
    (user: User, checked: boolean) => {
      void patchUser({
        variables: {
          id: user.id,
          singleParent: checked,
        },
        optimisticResponse: {
          patchUserMutation: {
            user: {
              __typename: 'UserNode',
              id: user.id,
              childcare: {
                __typename: 'ChildcareNode',
                numberOfChildren: user.childcare.numberOfChildren,
                singleParent: checked,
                childChronicIllness: user.childcare.childChronicIllness,
              },
            },
          },
        },
      })
    },
    [patchUser]
  )

  const handleToggleChildChronicIllness = useCallback(
    (user: User, checked: boolean) => {
      void patchUser({
        variables: {
          id: user.id,
          childChronicIllness: checked,
        },
        optimisticResponse: {
          patchUserMutation: {
            user: {
              __typename: 'UserNode',
              id: user.id,
              childcare: {
                __typename: 'ChildcareNode',
                numberOfChildren: user.childcare.numberOfChildren,
                singleParent: user.childcare.singleParent,
                childChronicIllness: checked,
              },
            },
          },
        },
      })
    },
    [patchUser]
  )

  const handleChangeNumberOfChildren = useCallback(
    (user: User, value: string) => {
      const numberOfChildren = Number(value)
      if (isNaN(numberOfChildren))
        throw new Error('Invalid number of children: ' + value)

      void patchUser({
        variables: {
          id: user.id,
          numberOfChildren,
        },
        optimisticResponse: {
          patchUserMutation: {
            user: {
              __typename: 'UserNode',
              id: user.id,
              childcare: {
                __typename: 'ChildcareNode',
                numberOfChildren,
                singleParent: user.childcare.singleParent,
                childChronicIllness: user.childcare.childChronicIllness,
              },
            },
          },
        },
      })
    },
    [patchUser]
  )

  return (
    <Section header={translations.header} loading={loading}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center p-4">
            <Loader.Dots />
          </div>
        }
      >
        <ChildcareCardInner
          onToggleSingleParent={handleToggleSingleParent}
          onToggleChildChronicIllness={handleToggleChildChronicIllness}
          onChangeNumberOfChildren={handleChangeNumberOfChildren}
        />
      </Suspense>
    </Section>
  )
}
